import { Colors, htmlToSlate, InlineEditableText, RichText, Text } from '@amelio/ui-library';
import { useState } from 'react';

import type { ThematicMeeting } from '@/types/thematic-meetings';

import { useUpdateThematicMeetingContext } from '@/api/thematic-meetings';
import { useAmelioTranslations } from '@/hooks';
import { FIELD_LABEL } from '@/pages/ThematicMeetings/components/meetingStyles';
import { CONTEXT_MAX_LENGTH } from '@/pages/ThematicMeetings/constants';
import { useSaveThematicMeeting } from '@/pages/ThematicMeetings/hooks/useSaveThematicMeeting';
import { usePendingSave } from '@/pages/ThematicMeetings/Plan/components/PendingSavesScope';
import { validateMeetingContext } from '@/pages/ThematicMeetings/utils/meetingRichText';
import { showErrorToast } from '@/plugins/Toast';
import { htmlToPlainText } from '@/utils/htmlSanitizer';

interface MeetingContextFieldProps {
    meeting: ThematicMeeting;
    language: string;
}

/**
 * The context the manager wrote for the meeting, edited right where it reads while the meeting is
 * planned. A completed meeting shows it as a record, and nothing at all when it has none.
 */
export const MeetingContextField = ({ meeting, language }: MeetingContextFieldProps) => {
    const { t } = useAmelioTranslations();
    const { saveInPlace } = useSaveThematicMeeting();
    const { mutateAsync: updateContext } = useUpdateThematicMeetingContext();
    const isCompleted = meeting.status === 'Completed';
    const [draft, setDraft] = useState<string | null>(null);

    // An emptied editor still holds an empty paragraph: the meeting then has no context at all. The
    // creation flow's bounds apply here too; a rejection keeps the draft open under its message.
    const saveContext = (html: string) => {
        const context = htmlToPlainText(html).trim() ? html : null;
        if (context === meeting.context) {
            return undefined;
        }
        const errorMessage = validateMeetingContext(context ?? '', t);
        if (errorMessage) {
            return Promise.reject(new Error(errorMessage));
        }
        return saveInPlace(() => updateContext({ id: meeting.id, context }));
    };

    // Completing saves the draft still open here. That save does not come from the field, which
    // shows no message for it, so a refusal is reported in a toast and the meeting stays open.
    const saveDraftBeforeCompleting = async (html: string) => {
        try {
            await saveContext(html);
            return true;
        } catch (refusal) {
            showErrorToast({
                title: t('errors.genericTitle'),
                message: (refusal as Error).message,
            });
            return false;
        }
    };

    usePendingSave(!isCompleted && draft !== null ? () => saveDraftBeforeCompleting(draft) : null);

    if (isCompleted && !meeting.context) {
        return null;
    }

    return (
        <div className={'mt-6 flex flex-col gap-1.5'}>
            <Text
                weight={'medium'}
                color={Colors.BASE_600}
                className={FIELD_LABEL}
            >
                {t('thematicMeetings.plan.context')}
            </Text>

            {isCompleted ? (
                <RichText
                    value={htmlToSlate(meeting.context ?? '')}
                    readOnly
                    toolbar={false}
                    // A record, not an editing surface: the editor's default minimum height would
                    // leave a wide gap under a one-line context.
                    contentMinHeight={'auto'}
                    locale={language}
                    dataTestId={'thematic-meeting-context-content'}
                />
            ) : (
                <InlineEditableText
                    variant={'rich'}
                    value={meeting.context ?? ''}
                    onSave={saveContext}
                    onDraftChange={setDraft}
                    editLabel={t('thematicMeetings.plan.editContext')}
                    fieldLabel={t('thematicMeetings.newMeeting.contextLabel')}
                    placeholder={t('thematicMeetings.plan.contextPlaceholder')}
                    maxLength={CONTEXT_MAX_LENGTH}
                    locale={language}
                    saveLabel={t('common.save')}
                    cancelLabel={t('common.cancel')}
                    dataTestId={'thematic-meeting-context'}
                />
            )}
        </div>
    );
};
